import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Api from "services/api";
import ConfirmationModal from 'components/common/ConfirmationMessage';
import InvoicePdfForm from "components/Pdf/InvoicePdfForm";
import toast from "react-hot-toast";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import FullPageLoader from 'components/common/FullPageLoader';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import { CustomSwitch } from 'components/common/Switch';
import { useTranslation } from 'react-i18next';

const InvoiceHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buttonLoader, setButtonLoader] = useState(false);
  const historyRef = useRef(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState(null);

  const [showDeleted, setShowDeleted] = useState(false);

  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreTargetIndex, setRestoreTargetIndex] = useState(null);

  const [makeCurrentModalOpen, setMakeCurrentModalOpen] = useState(false);
  const [makeCurrentLoading, setMakeCurrentLoading] = useState(false);
  const [makeCurrentTarget, setMakeCurrentTarget] = useState(null);
  const [makeCurrentTargetIndex, setMakeCurrentTargetIndex] = useState(null);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await Api.fetchInvoiceHistory(id);
      if (response?.status >= 200 && response?.status < 300) {
        setInvoice(response.data.data.invoice);
        setHistories(response.data.data.histories || []);
      } else {
        toast.error(response?.data?.message || t('invoiceHistory.loadFailed'));
      }
    } catch (error) {
      console.error(error);
      toast.error(t('invoiceHistory.fetchFailed'));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (id) {
      fetchHistory();
    }
  }, [id]);

  const openDeleteConfirm = (historyId, index) => {
    setDeleteTarget(historyId);
    setDeleteTargetIndex(index);
    setConfirmModalOpen(true);
  };

  const closeDeleteConfirm = () => {
    setConfirmModalOpen(false);
    setDeleteTarget(null);
    setDeleteTargetIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const response = await Api.deleteInvoiceHistory(deleteTarget);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success(t('invoiceHistory.revisionDeleted'), { className: 'successToast' });
        await fetchHistory();
      } else {
        toast.error(response?.data?.message || t('invoiceHistory.deleteFailed'));
      }
    } catch (err) {
      toast.error(err?.message || t('invoiceHistory.cancelAppointmentError'));
    } finally {
      setDeleteLoading(false);
      closeDeleteConfirm();
    }
  };


  const openRestoreConfirm = (historyId, index) => {
      setRestoreTarget(historyId);
      setRestoreTargetIndex(index);
      setRestoreModalOpen(true);
  };

  const closeRestoreConfirm = () => {
      setRestoreModalOpen(false);
      setRestoreTarget(null);
      setRestoreTargetIndex(null);
  };

  const handleConfirmRestore = async () => {
      if (!restoreTarget) return;
      setRestoreLoading(true);
      try {
        const response = await Api.restoreInvoiceHistory(restoreTarget);
        if (response?.status >= 200 && response?.status < 300) {
          toast.success(t('invoiceHistory.revisionRestored'), { className: 'successToast' });
          await fetchHistory();
        } else {
          toast.error(response?.data?.message || t('invoiceHistory.restoreFailed'));
        }
      } catch (err) {
        toast.error(err?.message || t('invoiceHistory.restoreError'));
      } finally {
        setRestoreLoading(false);
        closeRestoreConfirm();
      }
  };

  const openMakeCurrentConfirm = (historyId, index) => {
      setMakeCurrentTarget(historyId);
      setMakeCurrentTargetIndex(index);
      setMakeCurrentModalOpen(true);
  };

  const closeMakeCurrentConfirm = () => {
      setMakeCurrentModalOpen(false);
      setMakeCurrentTarget(null);
      setMakeCurrentTargetIndex(null);
  };

  const handleConfirmMakeCurrent = async () => {
      if (!makeCurrentTarget) return;
      setMakeCurrentLoading(true);
      try {
        const response = await Api.makeHistoryCurrent(makeCurrentTarget);
        if (response?.status >= 200 && response?.status < 300) {
          toast.success(t('invoiceHistory.makeCurrentSuccess'), { className: 'successToast' });
          await fetchHistory();
        } else {
          toast.error(response?.data?.message || t('invoiceHistory.makeCurrentFailed'));
        }
      } catch (err) {
        toast.error(err?.message || t('invoiceHistory.makeCurrentError'));
      } finally {
        setMakeCurrentLoading(false);
        closeMakeCurrentConfirm();
      }
  };


  const historyCount = useMemo(() => histories.length, [histories]);

  const handleGeneratePdf = async () => {
    if (!historyRef.current) {
      toast.error(t('invoiceHistory.nothingToDownload'), { className: "errorToast" });
      return;
    }

    setButtonLoader(true);

    try {
      const doc = new jsPDF('p', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const usableWidth = pageWidth - margin * 2;

      // Har ".page" block ko alag se capture karte hain (BEFORE/AFTER cards)
      const pageElements = historyRef.current.querySelectorAll('.page');

      if (pageElements.length === 0) {
        toast.error(t('invoiceHistory.nothingToDownload'), { className: "errorToast" });
        setButtonLoader(false);
        return;
      }

      let isFirstPage = true;

      for (const el of pageElements) {
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          ignoreElements: (element) => element.classList?.contains('pdf-hide-buttons'),
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = usableWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        if (!isFirstPage) {
          doc.addPage();
        }
        isFirstPage = false;

        // Agar element ki height ek page se zyada ho to slice karke multiple pages mein daalo
        doc.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
        heightLeft -= (pageHeight - margin * 2);

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          doc.addPage();
          doc.addImage(imgData, 'PNG', margin, position + margin, imgWidth, imgHeight);
          heightLeft -= (pageHeight - margin * 2);
        }
      }

      doc.save(`invoice-${invoice?.invoice_number || id}-history.pdf`);
      toast.success(t('invoiceHistory.downloadSuccess'), {
        className: "successToast",
      });
    } catch (error) {
      console.error(error);
      toast.error(t('invoiceHistory.generatePdfFailed'), { className: "errorToast" });
    } finally {
      setButtonLoader(false);
    }
  };

  const activeHistories = histories.filter((h) => h.event !== 'invoice_deleted');

  const renderHistoryCard = (history, index) => {
      const isDeleted = history.event === 'invoice_deleted';

      // if (isDeleted && !showDeleted) return null;

      const isCurrent = history.is_current;
      const isManualCurrent = history.is_manual_current;

      return (
        <div key={history.id || index} className="invoice-history-version-card">
          <div className="invoice-history-versions">
            {history.before && (
            <div className="invoice-history-version view_pdf_main">
              <InvoicePdfForm
                data={history.before}
                loader={false}
                type={history.before?.invoice_type || history.invoice_type}
                isOriginal={true}
                versionNumber="1.0"
              />
            </div>
            )}

            {history.after && (!isDeleted || showDeleted) && (
            <div className={`invoice-history-version view_pdf_main ${isCurrent ? 'current-invoice' : ''}`}>
              <InvoicePdfForm
                data={history.after}
                loader={false}
                type={history.after?.invoice_type || history.invoice_type}
                isOriginal={false}
                versionNumber={`1.${index + 1}`}
                editedDate={history.created_at}
                isCurrent={isCurrent}
                isManualCurrent={isManualCurrent}
                isDeleted={isDeleted}
                showDelete={!isCurrent && !isDeleted}
                showRestore={isDeleted}
                showMakeCurrent={!isCurrent && !isDeleted}
                onDeleteClick={() => openDeleteConfirm(history.id, index)}
                onRestoreClick={() => openRestoreConfirm(history.id, index)}
                onMakeCurrentClick={() => openMakeCurrentConfirm(history.id, index)}
                user={history.user}
              />
            </div>
            )}
          </div>
        </div>
      );
  };

  return (
    <div className="content">
      <div className="row_savebtn">
          <div className="d-flex align-items-center gap-2">
            <button className="main_heading back_button" onClick={() => navigate(-1)}>
              <i className="fa-solid fa-arrow-left"></i> {t('common.back')}
            </button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0" style={{color: '#6C889C' , fontWeight: '700'}}>
                {t('invoiceHistory.showDeletedInvoices')}
              </label>
              <CustomSwitch 
                checked={showDeleted}
                handleChange={setShowDeleted}
                labelText={t('invoiceHistory.showDeletedInvoices')}
              />
            </div>
            <SubmitButton
                handleSubmit={handleGeneratePdf}
                buttonType={'button'}
                loader={buttonLoader}
                btnText={t('invoiceHistory.downloadInvoice')}
                disabled={showDeleted}
            />
          </div>
      </div>

      {loading ? (
        <FullPageLoader />
      ) : (
        <>
          {historyCount === 0 ? (
            <div className="empty-state">{t('invoiceHistory.noHistoryFound')}</div>
          ) : (
            <div className="invoice-history-versions-list" ref={historyRef}>
              {histories.map((history, index) => renderHistoryCard(history, index))}
            </div>
          )}
        </>
      )}
      {confirmModalOpen && (
        <ConfirmationModal
          isOpen={confirmModalOpen}
          onClose={closeDeleteConfirm}
          onConfirm={handleConfirmDelete}
          loading={deleteLoading}
          message={t('invoiceHistory.confirmDeleteRevision', { version: deleteTargetIndex !== null ? `1.${deleteTargetIndex + 1}` : '' })}
        />
      )}

      {restoreModalOpen && (
        <ConfirmationModal
          isOpen={restoreModalOpen}
          onClose={closeRestoreConfirm}
          onConfirm={handleConfirmRestore}
          loading={restoreLoading}
          message={t('invoiceHistory.confirmRestoreRevision', { version: restoreTargetIndex !== null ? `1.${restoreTargetIndex + 1}` : '' })}
        />
      )}

      {makeCurrentModalOpen && (
        <ConfirmationModal
          isOpen={makeCurrentModalOpen}
          onClose={closeMakeCurrentConfirm}
          onConfirm={handleConfirmMakeCurrent}
          loading={makeCurrentLoading}
          message={t('invoiceHistory.confirmMakeCurrentRevision', { version: makeCurrentTargetIndex !== null ? `1.${makeCurrentTargetIndex + 1}` : '' })}
        />
      )}
    </div>
  );
};

export default InvoiceHistory;